import React, { useEffect, useState } from 'react';
import Select, { MultiValue, StylesConfig } from 'react-select';

interface AlleleOption {
    value: string;
    label: string;
}

interface AlleleSearchSelectProps {
    value: string[];
    onValueChange: (value: string[]) => void;
}

const MAX_ALLELES = 6;
const ALLELE_LIST_URL = `${process.env.PUBLIC_URL || ''}/mhcattention_pseudoseqs.tsv`;
const FORM_FONT_FAMILY = '"MontRegular", sans-serif';

const customStyles: StylesConfig<AlleleOption, true> = {
    control: (provided) => ({
        ...provided,
        minHeight: 44,
        borderColor: '#ccc',
        boxShadow: 'none',
        fontFamily: FORM_FONT_FAMILY,
    }),
    input: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
    }),
    valueContainer: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
    }),
    placeholder: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
    }),
    menu: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
    }),
    option: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
        fontWeight: 'normal',
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#eef4ff',
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
        fontWeight: 'normal',
    }),
    noOptionsMessage: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
    }),
    menuPortal: (provided) => ({
        ...provided,
        fontFamily: FORM_FONT_FAMILY,
        zIndex: 9999,
    }),
};

const AlleleSearchSelect: React.FC<AlleleSearchSelectProps> = ({ value, onValueChange }) => {
    const [items, setItems] = useState<AlleleOption[]>([]);

    useEffect(() => {
        const fetchAlleles = async () => {
            try {
                const response = await fetch(ALLELE_LIST_URL);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const text = await response.text();
                const nextItems = Array.from(
                    new Set(
                        text
                            .split(/\r?\n/)
                            .map((line) => line.split('\t')[0]?.trim())
                            .filter(Boolean)
                    )
                ).map((item) => ({
                    value: item as string,
                    label: item as string,
                }));

                setItems(nextItems);
            } catch (error) {
                console.error('Failed to fetch MHC Attention allele list', error);
            }
        };

        void fetchAlleles();
    }, []);

    const selectedItems = items.filter((item) => value.includes(item.value));

    return (
        <Select
            isMulti
            options={items}
            value={selectedItems}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            placeholder="Search MHC alleles..."
            onChange={(selected: MultiValue<AlleleOption>) => {
                onValueChange(selected.map((item) => item.value));
            }}
            isOptionDisabled={(option) => value.length >= MAX_ALLELES && !value.includes(option.value)}
            styles={customStyles}
            menuPortalTarget={document.body}
        />
    );
};

export default AlleleSearchSelect;
